import axios from 'axios';
import { validateResponse } from './validateResponse';
import {
  DashboardOrdersPerUserResponseSchema,
  DashboardActivityResponseSchema,
  PastPerformanceResponseSchema,
  PastPerformanceProgressEventSchema,
} from '../schemas/apiResponse.schemas';
import type {
  DashboardOrdersPerUserResponse,
  DashboardActivityResponse,
  PastPerformanceResponse,
  PastPerformanceProgressEvent,
} from '../schemas/apiResponse.schemas';

export interface DashboardOrdersPerUserParams {
  start: string;
  end: string;
  timeZone?: string;
}

export async function fetchOrdersPerUser(
  params: DashboardOrdersPerUserParams,
): Promise<DashboardOrdersPerUserResponse> {
  const response = await axios.get('/api/dashboard/orders-per-user', {
    params: {
      start: params.start,
      end: params.end,
      timeZone: params.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });
  return validateResponse(
    DashboardOrdersPerUserResponseSchema,
    response.data,
    'GET /api/dashboard/orders-per-user',
  );
}

export async function fetchActivity(
  params: DashboardOrdersPerUserParams,
): Promise<DashboardActivityResponse> {
  const response = await axios.get('/api/dashboard/activity', {
    params: {
      start: params.start,
      end: params.end,
      timeZone: params.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });
  return validateResponse(
    DashboardActivityResponseSchema,
    response.data,
    'GET /api/dashboard/activity',
  );
}

export interface DashboardDateRangeParams {
  start: string;
  end: string;
}

export async function fetchPastPerformance(
  params: DashboardDateRangeParams,
): Promise<PastPerformanceResponse> {
  const response = await axios.get('/api/dashboard/past-performance', {
    params: {
      start: params.start,
      end: params.end,
    },
  });
  return validateResponse(
    PastPerformanceResponseSchema,
    response.data,
    'GET /api/dashboard/past-performance',
  );
}

export type PastPerformanceProgressCallback = (event: PastPerformanceProgressEvent) => void;

export function fetchPastPerformanceStream(
  params: DashboardDateRangeParams,
  onProgress: PastPerformanceProgressCallback,
): Promise<PastPerformanceResponse> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = <T,>(fn: (arg: T) => void, arg: T): void => {
      if (settled) return;
      settled = true;
      es.close();
      fn(arg);
    };

    const query = new URLSearchParams({ start: params.start, end: params.end });
    const url = `/api/dashboard/past-performance-stream?${query.toString()}`;
    const base = window.location.origin;
    const fullUrl = url.startsWith('http') ? url : `${base}${url.startsWith('/') ? '' : '/'}${url}`;
    const es = new EventSource(fullUrl);

    es.addEventListener('progress', (e) => {
      try {
        const messageEvent = e as MessageEvent<string>;
        const parsed = PastPerformanceProgressEventSchema.safeParse(JSON.parse(messageEvent.data));
        if (parsed.success) {
          onProgress(parsed.data);
        }
      } catch {
        /* ignore malformed progress events */
      }
    });

    es.addEventListener('result', (e) => {
      try {
        const messageEvent = e as MessageEvent<string>;
        const result = validateResponse(
          PastPerformanceResponseSchema,
          JSON.parse(messageEvent.data),
          'GET /api/dashboard/past-performance-stream',
        );
        finish(resolve, result);
      } catch (err: unknown) {
        finish(reject, err);
      }
    });

    es.addEventListener('error', (e: Event) => {
      const messageEvent = e as MessageEvent<string>;
      const msg = messageEvent.data
        ? (() => {
            try {
              const payload = JSON.parse(messageEvent.data) as { error?: string };
              return payload.error;
            } catch {
              return null;
            }
          })()
        : null;
      finish(reject, new Error(msg || 'Connection lost'));
    });
  });
}
